import React, { useState, useEffect } from "react";

/**
 * Spy Journal Modal - Shows collected clues in a handwritten notebook style
 * Used for Chicago tour's attribute collection system
 */
export function SpyJournalModal({ tour, progressHook, justSolvedId, onClose, embedded = false }) {
  const [isClosing, setIsClosing] = useState(false);
  const [showNewEntry, setShowNewEntry] = useState(false);

  // Exclude the final step (decode location) from journal entries
  const journalSteps = tour.steps.slice(0, -1);
  const totalClues = journalSteps.length;

  // Get all solved steps in order (excluding final step)
  const solvedSteps = journalSteps.filter(step =>
    progressHook.isAnsweredCorrect(step) === true
  );

  // Sort by step order
  const orderedSolvedSteps = solvedSteps.sort((a, b) => {
    const indexA = journalSteps.findIndex(s => s.id === a.id);
    const indexB = journalSteps.findIndex(s => s.id === b.id);
    return indexA - indexB;
  });

  // Find the step that was just solved
  const justSolvedStep = journalSteps.find(step => step.id === justSolvedId);

  // Check if all clues collected
  const allCluesCollected = solvedSteps.length === totalClues;

  // Animate new entry
  useEffect(() => {
    if (justSolvedStep && !embedded) {
      const timer = setTimeout(() => {
        setShowNewEntry(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [justSolvedStep, embedded]);

  const handleClose = () => {
    if (embedded) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose(allCluesCollected);
    }, 300);
  };

  const journalContent = (
    <div
      className="w-full max-w-md rounded-lg overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #f5f0e1 0%, #e8e0c8 100%)',
        border: '3px solid #10B981',
        boxShadow: embedded ? 'none' : '0 10px 40px rgba(0, 0, 0, 0.5), inset 0 0 30px rgba(16, 185, 129, 0.2)'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Import handwriting font */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;600;700&display=swap');

        .spy-journal-entry {
          font-family: 'Caveat', cursive;
        }
        .spy-journal-entry.new-entry {
          animation: newEntryPulse 1s ease-out forwards;
        }
        @keyframes newEntryPulse {
          0% {
            opacity: 0;
            transform: scale(1.1);
            background: rgba(16, 185, 129, 0.3);
          }
          50% {
            opacity: 1;
            background: rgba(16, 185, 129, 0.2);
          }
          100% {
            opacity: 1;
            transform: scale(1);
            background: transparent;
          }
        }
      `}</style>

      {/* Journal Header */}
      <div
        className="px-4 py-3 text-center"
        style={{
          background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
          borderBottom: '2px solid #059669'
        }}
      >
        <div
          className="text-lg font-bold tracking-wider"
          style={{
            color: '#ffffff',
            fontFamily: 'monospace',
            textShadow: '0 0 10px rgba(255, 255, 255, 0.3)'
          }}
        >
          FIELD NOTES
        </div>
        <div
          className="text-xs tracking-widest mt-1"
          style={{ color: 'rgba(255, 255, 255, 0.8)' }}
        >
          OPERATION WINDY CITY
        </div>
      </div>

      {/* Journal Body */}
      <div
        style={{
          minHeight: embedded ? 'auto' : '200px',
          maxHeight: embedded ? 'none' : '300px',
          overflowY: 'auto',
          padding: '1rem',
          background: '#f5f0e1'
        }}
      >
        {orderedSolvedSteps.length === 0 ? (
          <div
            className="text-center py-8"
            style={{
              fontFamily: 'Caveat, cursive',
              fontSize: '1.25rem',
              color: '#666',
              fontStyle: 'italic'
            }}
          >
            No clues collected yet...
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {orderedSolvedSteps.map((step, idx) => {
              const stepIndex = journalSteps.findIndex(s => s.id === step.id);
              const isNewEntry = step.id === justSolvedId && showNewEntry;
              const isJustSolved = step.id === justSolvedId;

              return (
                <div
                  key={step.id}
                  className={`spy-journal-entry ${isNewEntry ? 'new-entry' : ''} py-1`}
                  style={{
                    fontFamily: 'Caveat, cursive',
                    borderBottom: '1px solid rgba(16, 185, 129, 0.2)'
                  }}
                >
                  <div className="flex items-baseline gap-2">
                    <span
                      className="text-xs font-bold"
                      style={{
                        color: '#666',
                        minWidth: '1.2rem'
                      }}
                    >
                      {stepIndex + 1}.
                    </span>
                    <span
                      className="text-lg font-bold truncate"
                      style={{
                        color: isJustSolved ? '#10B981' : '#333',
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em'
                      }}
                    >
                      {embedded && step.displaySolution ? step.displaySolution : step.solution}
                    </span>
                    {embedded && step.solutionHint && (
                      <span
                        className="text-xs"
                        style={{
                          color: '#999',
                          fontFamily: 'sans-serif',
                          textTransform: 'uppercase'
                        }}
                      >
                        {step.solutionHint}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Placeholder lines for remaining clues */}
        {!embedded && orderedSolvedSteps.length < totalClues && (
          <div className={`grid grid-cols-2 gap-x-4 gap-y-1 ${orderedSolvedSteps.length > 0 ? 'mt-0' : ''}`}>
            {Array.from({ length: totalClues - orderedSolvedSteps.length }).map((_, idx) => (
              <div
                key={`placeholder-${idx}`}
                className="py-1"
                style={{
                  opacity: 0.4,
                  borderBottom: '1px solid rgba(16, 185, 129, 0.1)'
                }}
              >
                <div className="flex items-baseline gap-2">
                  <span
                    className="text-xs"
                    style={{
                      color: '#999',
                      minWidth: '1.2rem',
                      fontFamily: 'Caveat, cursive'
                    }}
                  >
                    {orderedSolvedSteps.length + idx + 1}.
                  </span>
                  <span
                    className="text-lg"
                    style={{
                      color: '#bbb',
                      fontFamily: 'Caveat, cursive'
                    }}
                  >
                    ???
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>


      {/* Continue Button (only in modal mode) */}
      {!embedded && (
        <div className="p-4" style={{ background: 'rgba(16, 185, 129, 0.05)' }}>
          <button
            onClick={handleClose}
            className="w-full py-3 rounded-lg font-semibold text-sm uppercase tracking-wide transition-all"
            style={{
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              color: '#ffffff',
              border: 'none',
              boxShadow: '0 0 15px rgba(16, 185, 129, 0.3)'
            }}
          >
            {allCluesCollected ? 'Decode Final Answer' : 'Continue Mission'}
          </button>
        </div>
      )}
    </div>
  );

  // If embedded, just return the content without modal wrapper
  if (embedded) {
    return journalContent;
  }

  // Modal wrapper
  return (
    <div
      className={`fixed z-50 flex items-center justify-center ${isClosing ? 'modal-fade-out' : 'modal-fade-in'}`}
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(8px)',
        padding: 'calc(env(safe-area-inset-top, 0px) + 1rem) 1rem calc(env(safe-area-inset-bottom, 0px) + 1rem)'
      }}
      onClick={handleClose}
    >
      <style>{`
        .modal-fade-in {
          animation: modalFadeIn 0.3s ease-out;
        }
        .modal-fade-out {
          animation: modalFadeOut 0.3s ease-out forwards;
        }
        @keyframes modalFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes modalFadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
      `}</style>
      {journalContent}
    </div>
  );
}
